import React from "react"
import moment, { Moment } from "moment"
import { Loader2, Plus } from "lucide-react"
import styles from "./EventCalendar.module.scss"

import { Area } from "~/types/sponsorship/Area"

type CalendarView = "month" | "week" | "day"

type SponsorshipEntry = {
  id: string
  title: string
  sponsor: string
  startDate: string
  endDate: string
  areaId: string
}

type EventCalendarProps = {
  view: CalendarView
  startDate: Moment
  endDate: Moment
  areas?: Area[]
  entries?: SponsorshipEntry[]
  isLoading?: boolean
  onNewArea?: () => void
  onAreaClick?: (area: Area) => void
  onCellClick?: (area: Area, entryId?: string, date?: Moment) => void
}

const EventCalendar = ({
  view,
  startDate,
  endDate,
  areas = [],
  entries = [],
  isLoading = false,
  onNewArea,
  onAreaClick,
  onCellClick,
}: EventCalendarProps) => {
  const generateDateColumns = () => {
    const dates: Moment[] = []
    const current = startDate.clone()

    while (current.isSameOrBefore(endDate, "day")) {
      dates.push(current.clone())
      current.add(1, "day")
    }

    return dates
  }

  const dateColumns = generateDateColumns()

  // Calculate grid template columns to prevent wrapping
  const getGridTemplateColumns = () => {
    const dateColumnCount = dateColumns.length
    const minColumnWidth = view === "day" ? "150px" : view === "week" ? "80px" : "24px"
    return `200px repeat(${dateColumnCount}, minmax(${minColumnWidth}, 1fr))`
  }

  const formatHeaderDate = (date: Moment) => {
    switch (view) {
      case "month":
        return date.format("D")
      case "week":
        return `${date.format("ddd")}\n${date.format("D")}`
      case "day":
        return date.format("MMM D, YYYY")
      default:
        return date.format("D")
    }
  }

  const getEntryForCell = (areaId: string, date: Moment) => {
    return entries.find(
      (entry) =>
        entry.areaId === areaId &&
        date.isBetween(moment(entry.startDate), moment(entry.endDate), "day", "[]")
    )
  }

  // Calculate entry spans for an area
  const getEntrySpans = (areaId: string) => {
    const areaEntries = entries.filter((entry) => entry.areaId === areaId)
    const spans: Array<{
      entry: SponsorshipEntry
      startColumn: number
      endColumn: number
      gridColumn: string
    }> = []

    areaEntries.forEach((entry) => {
      const entryStartDate = moment(entry.startDate)
      const entryEndDate = moment(entry.endDate)

      // Check if entry overlaps with visible date range
      const visibleStartDate = dateColumns[0]
      const visibleEndDate = dateColumns[dateColumns.length - 1]

      // Skip if entry doesn't overlap with visible range
      if (
        entryEndDate.isBefore(visibleStartDate, "day") ||
        entryStartDate.isAfter(visibleEndDate, "day")
      ) {
        return
      }

      // Calculate visible portion of the entry
      let visibleStartColumn = -1
      let visibleEndColumn = -1

      // Find start column
      for (let i = 0; i < dateColumns.length; i++) {
        if (dateColumns[i].isSameOrAfter(entryStartDate, "day")) {
          visibleStartColumn = i
          break
        }
      }

      // Find end column - look for the last date that is same or before entry end
      for (let i = dateColumns.length - 1; i >= 0; i--) {
        if (dateColumns[i].isSameOrBefore(entryEndDate, "day")) {
          visibleEndColumn = i
          break
        }
      }

      // Handle edge cases
      if (visibleStartColumn === -1) visibleStartColumn = 0
      if (visibleEndColumn === -1) visibleEndColumn = dateColumns.length - 1

      // Convert to grid columns (adding 2 because grid is 1-indexed and first column is area name)
      const startColumn = visibleStartColumn + 2
      const endColumn = visibleEndColumn + 2

      if (startColumn >= 2 && endColumn >= 2 && startColumn <= endColumn) {
        spans.push({
          entry,
          startColumn,
          endColumn,
          gridColumn: `${startColumn} / ${endColumn + 1}`, // CSS Grid span syntax
        })
      }
    })

    return spans
  }

  // Check if a date column is covered by any entry span
  const isDateCoveredByEntry = (areaId: string, dateIndex: number) => {
    const spans = getEntrySpans(areaId)
    const columnIndex = dateIndex + 2 // Convert to grid column index

    return spans.some((span) => columnIndex >= span.startColumn && columnIndex <= span.endColumn)
  }

  const handleAreaClick = (area: Area) => {
    onAreaClick?.(area)
  }

  const handleCellClick = (area: Area, entryId?: string, date?: Moment) => {
    onCellClick?.(area, entryId, date)
  }

  return (
    <div className={styles.calendar}>
      {isLoading && (
        <div className={styles.loadingOverlay}>
          <Loader2 className="spinner" style={{ width: "48px", height: "48px" }} />
        </div>
      )}
      <div
        className={`${styles.grid} ${isLoading ? styles.loading : ""}`}
        style={{ gridTemplateColumns: getGridTemplateColumns() }}
      >
        {/* Header Row */}
        <div className={styles.headerRow}>
          <div className={styles.areaHeader} />
          {dateColumns.map((date, index) => (
            <div key={index} className={styles.dateHeader}>
              {formatHeaderDate(date)}
            </div>
          ))}
        </div>

        {/* Area Rows */}
        {areas.map((area) => {
          return (
            <div key={area.id} className={styles.areaRow}>
              {/* Area Name */}
              <div className={styles.areaName} onClick={() => handleAreaClick(area)}>
                {area.name}
              </div>

              {/* Render entries based on view type */}
              {view === "day" ? (
                // Day view: Simple cell-by-cell rendering
                dateColumns.map((date, dateIndex) => {
                  const entry = getEntryForCell(area.id, date)
                  return (
                    <div
                      key={dateIndex}
                      className={`${styles.cell} ${entry ? styles.occupied : styles.empty}`}
                      onClick={() => handleCellClick(area, entry?.id, date)}
                    >
                      {entry && (
                        <div className={styles.entry}>
                          <div className={styles.entryTitle}>{entry.title}</div>
                          <div className={styles.entrySponsor}>{entry.sponsor}</div>
                        </div>
                      )}
                    </div>
                  )
                })
              ) : (
                // Month/Week view: Use spanning entries
                <>
                  {/* Spanning Entry Blocks */}
                  {!isLoading &&
                    getEntrySpans(area.id).map((span, spanIndex) => (
                      <div
                        key={`${area.id}-entry-${spanIndex}`}
                        className={`${styles.cell} ${styles.occupied} ${styles.spanningEntry}`}
                        style={{ gridColumn: span.gridColumn }}
                        onClick={() =>
                          handleCellClick(area, span.entry.id, moment(span.entry.startDate))
                        }
                      >
                        <div className={styles.entry}>
                          <div className={styles.entryTitle}>{span.entry.title}</div>
                          <div className={styles.entrySponsor}>{span.entry.sponsor}</div>
                        </div>
                      </div>
                    ))}

                  {/* Empty Cells for uncovered dates */}
                  {dateColumns.map((date, dateIndex) => {
                    const isCovered = isDateCoveredByEntry(area.id, dateIndex)

                    if (isCovered) {
                      return null // Skip rendering, covered by spanning entry
                    }

                    return (
                      <div
                        key={dateIndex}
                        className={`${styles.cell} ${styles.empty}`}
                        onClick={() => handleCellClick(area, undefined, date)}
                      />
                    )
                  })}
                </>
              )}
            </div>
          )
        })}

        {/* New Area Row */}
        <div className={styles.newAreaRow}>
          <div className={styles.newAreaButton} onClick={onNewArea}>
            <span>New area</span>
            <Plus size={16} />
          </div>
          {dateColumns.map((_, index) => (
            <div key={index} className={styles.emptyCell} />
          ))}
        </div>
      </div>
    </div>
  )
}

export default EventCalendar
